import { Layout, Rect, Video, blur, brightness, contrast, grayscale, hue, saturate, sepia, type Filter, type Node } from "@revideo/2d";
import { anyTransition, cutAt, styleInto, type Clip } from "@hudhud/core";
import {
  WIDTH, HEIGHT, FIT_W, SRC_W, SRC_H, FPS, msToFrame, cropBox, pieceFrame, pieceSpans, gradeSteps, lookSteps, roomLook, vignetteAlpha,
  transitionFrames, incomingMotionValues, outgoingMotionValues, type LookStep, type MotionValues,
} from "@hudhud/reel/math";
import { ellipseRect } from "./gradients";

/**
 * How every Video node gets its frames: the renderer's ffmpeg bridge (exact, frame by frame), or the browser's own decoder in
 * the studio's live player, which has no bridge. One scene runs per page, so the reel sets this once before it builds a node.
 */
export const videoDecoder: { current: "ffmpeg" | "web" } = { current: "ffmpeg" };

/** The colour steps as canvas filters, in the look's own order. */
export function filtersOf(steps: LookStep[]): Filter[] {
  return steps.map((s) =>
    s.fn === "contrast" ? contrast(s.v) : s.fn === "saturate" ? saturate(s.v) : s.fn === "sepia" ? sepia(s.v) :
    s.fn === "brightness" ? brightness(s.v) : s.fn === "hue-rotate" ? hue(s.v) : grayscale(s.v));
}

/** The node properties a piece's transition motion sets: position, scale, opacity, blur. Absent values are neutral. */
export const motionProps = (m: MotionValues) => ({
  opacity: m.opacity ?? 1,
  position: [m.tx ?? 0, m.ty ?? 0] as [number, number],
  scale: m.scale ?? 1,
  filters: m.blur ? [blur(m.blur)] : [],
});

export interface PieceInput {
  clip: Clip;
  segIndex: number;
  mediaUrl: string;
  mediaStartMs: number;
  /** the piece's own frame (Remotion's `useCurrentFrame()` inside its Sequence) */
  frame: () => number;
  /** a still: the source frame at the piece's own `frame` is shown, nothing plays */
  still?: boolean;
  cutoutUrl?: string;
  lite?: boolean;
  /** words behind the speaker for this piece, drawn under the cutout */
  behind?: Node | null;
}

/** The source video node of one piece: at its in-point, playing (or frozen on the frame the still asks for). */
function sourceVideo(o: PieceInput, extra: Partial<ConstructorParameters<typeof Video>[0]> = {}): Video {
  const seg = o.clip.segments[o.segIndex];
  const inSec = Math.max(0, seg.inMs - o.mediaStartMs) / 1000;
  return new Video({
    src: o.mediaUrl, decoder: videoDecoder.current,
    time: o.still ? inSec + o.frame() / FPS : inSec,
    play: !o.still,
    ...extra,
  });
}

/** Centre of a crop box in the canvas' centre-origin coordinates. */
const boxCentre = (b: { left: number; top: number; width: number; height: number }): [number, number] => [b.left + b.width / 2 - WIDTH / 2, b.top + b.height / 2 - HEIGHT / 2];

/**
 * One piece of the cut video: face-follow crop with every zoom folded in, the cut punctuation (shake, flash, glitch, flicker),
 * the colour look, the treated room behind a cut-out speaker, and the vignette. Mirrors SegmentVideo in the Remotion reel.
 */
export function pieceNode(o: PieceInput): Node {
  const { clip, segIndex, frame, lite } = o;
  const grade = () => (lite ? [] : filtersOf(gradeSteps(clip.look.preset, clip.look.strength, pieceFrame(clip, segIndex, frame()).flicker)));

  if (clip.layout === "split") {
    const fgH = (WIDTH * SRC_H) / SRC_W;
    const fgY = HEIGHT * 0.4 - HEIGHT / 2; // the foreground's centre: its top at 40 % less half its height, as the CSS placed it
    const inSec = Math.max(0, clip.segments[segIndex].inMs - o.mediaStartMs) / 1000 + (o.still ? frame() / FPS : 0);
    const room = sourceVideo(o, { width: FIT_W * 1.1, height: HEIGHT * 1.1, filters: [blur(40), brightness(0.45)] });
    const front = sourceVideo(o, { width: WIDTH, height: fgH, y: fgY, shadowColor: "rgba(0,0,0,0.6)", shadowBlur: 80, shadowOffset: [0, 30] });
    const children: Node[] = [room, front];
    if (o.behind) children.push(o.behind);
    if (o.cutoutUrl) children.push(new Video({ src: o.cutoutUrl, decoder: videoDecoder.current, time: inSec, play: !o.still, width: WIDTH, height: fgH, y: fgY }));
    return new Layout({ width: WIDTH, height: HEIGHT, filters: () => (lite ? [] : filtersOf(lookSteps(clip.look.preset, clip.look.strength))), children });
  }

  const pf = () => pieceFrame(clip, segIndex, frame());
  const box = () => cropBox(pf().crop);
  const shake = () => pf().shake;
  const shaken = () => { const s = shake(); return s.x || s.y ? 1.04 : 1; };
  const bg = o.cutoutUrl && !lite ? clip.background : null;
  const room = roomLook(bg ?? { mode: "room", amount: 0, colour: "" });
  const roomFilters = () => {
    const base = grade();
    if (bg?.mode === "blur") return [...base, blur(room.blur!), brightness(room.brightness!)];
    if (bg?.mode === "dark") return [...base, brightness(room.brightness!), saturate(room.saturate!)];
    return base;
  };

  const layers: Node[] = [];
  // the room (unless a flat colour replaces it), then the glitch copies over it
  if (bg?.mode !== "colour") layers.push(sourceVideo(o, { width: () => box().width, height: () => box().height, position: () => boxCentre(box()), filters: roomFilters }));
  const glitchOn = !lite && (clip.effects.glitch || clip.direction.moments.some((m) => m.kind === "glitch"));
  if (glitchOn) {
    const g = () => pf().glitch;
    // clipPath inset(top% 0 bottom% 0): a band of the frame, the copy shifted sideways inside it
    const band = (topPct: () => number, bottomPct: () => number, dx: () => number, extra: Filter[]) => {
      const h = () => HEIGHT * (1 - (topPct() + bottomPct()) / 100);
      const cy = () => HEIGHT * (topPct() / 100) + h() / 2 - HEIGHT / 2;
      return new Rect({
        width: WIDTH, height: h, y: cy, clip: true, opacity: () => 0.6 * g(), compositeOperation: "screen",
        children: [sourceVideo(o, {
          width: () => box().width, height: () => box().height,
          position: () => { const c = boxCentre(box()); return [c[0] + dx(), c[1] - cy()]; },
          filters: () => [...grade(), ...extra],
        })],
      });
    };
    layers.push(band(() => 20 + g() * 10, () => 30 - g() * 10, () => -14 * g(), []));
    layers.push(band(() => 55 - g() * 8, () => 10 + g() * 8, () => 14 * g(), [hue(120)]));
  }
  const shakeWrap = (children: Node[]) => new Layout({ width: WIDTH, height: HEIGHT, position: () => [shake().x, shake().y], rotation: () => shake().rot, scale: shaken, children });

  const frame_: Node[] = [shakeWrap(layers)];
  if (o.behind || o.cutoutUrl) {
    // words behind the speaker: drawn over the room, then the speaker's cutout (same crop, same grade) is drawn over the words
    const over: Node[] = [];
    if (o.behind) over.push(o.behind);
    if (o.cutoutUrl) over.push(new Video({ src: o.cutoutUrl, decoder: videoDecoder.current, time: Math.max(0, clip.segments[segIndex].inMs - o.mediaStartMs) / 1000 + (o.still ? frame() / FPS : 0), play: !o.still,
      width: () => box().width, height: () => box().height, position: () => boxCentre(box()), filters: grade }));
    frame_.push(shakeWrap(over));
  }
  if (clip.look.vignette > 0) frame_.push(ellipseRect(WIDTH, HEIGHT, 0.5, 0.45, [{ offset: 0.55, color: "rgba(0,0,0,0)" }, { offset: 1, color: `rgba(0,0,0,${vignetteAlpha(clip.look.vignette)})` }]));
  frame_.push(new Rect({ width: WIDTH, height: HEIGHT, fill: "#fff", opacity: () => pf().flash }));

  return new Rect({ width: WIDTH, height: HEIGHT, clip: true, fill: bg?.mode === "colour" ? bg.colour : undefined, children: frame_ });
}

/** A piece inside a transition: it arrives over the one before and plays on while the next arrives; each cut has its own style. */
export function transitionPiece(clip: Clip, segIndex: number, span: { dur: number; overlap: number }, frame: () => number, lite: boolean, piece: Node): Node {
  const overlapIn = segIndex === 0 ? 0 : transitionFrames(clip, segIndex - 1);
  const isLast = segIndex === clip.segments.length - 1;
  const motion = () => {
    const local = frame();
    const enter = overlapIn > 0 ? local / overlapIn : 1;
    const leave = isLast || span.overlap <= 0 ? 0 : (local - (span.dur - span.overlap)) / span.overlap;
    const inM = incomingMotionValues(styleInto(clip, segIndex), enter, segIndex, lite);
    const outM = outgoingMotionValues(cutAt(clip, segIndex).style, leave, segIndex + 1, lite);
    return { ...inM, motion: { ...outM, ...inM.motion } }; // the incoming style wins the keys it sets, as the CSS spread did
  };
  return new Layout({
    width: WIDTH, height: HEIGHT,
    opacity: () => motionProps(motion().motion).opacity,
    position: () => motionProps(motion().motion).position,
    scale: () => motionProps(motion().motion).scale,
    filters: () => motionProps(motion().motion).filters,
    children: [piece, new Rect({ width: WIDTH, height: HEIGHT, fill: () => motion().overlayColor, opacity: () => motion().overlayOpacity })],
  });
}

export interface VideoLayerInput {
  clip: Clip; mediaUrl: string; mediaStartMs: number; cutoutUrl?: string; lite?: boolean;
  /** the composition frame */
  frame: () => number;
  /** the composition frame the video starts on */
  from: number;
  /** the behind-the-speaker words for a piece, on the piece's own clock */
  behind?: (pieceFrom: number, pieceFrames: number, frame: () => number) => Node | null;
}

/**
 * The kept pieces back to back (jump cuts / transitions). Pieces are nodes that exist only for their own frames: the driver calls
 * `tick` once per frame and the layer adds each piece on its first frame and removes it after its last, exactly as Remotion mounts
 * a Sequence — so an outgoing piece keeps playing under the incoming one for the overlap.
 */
export function videoLayer(o: VideoLayerInput): { node: Layout; tick: (frame: number) => void } {
  const spans = pieceSpans(o.clip);
  const transition = anyTransition(o.clip);
  const hasBehind = (o.clip.behindTexts ?? []).some((t) => t.enabled && t.text.trim());
  const node = new Layout({ width: WIDTH, height: HEIGHT });
  const live = new Map<number, Node>();
  const pieceLocal = (i: number) => () => o.frame() - o.from - spans[i].from;
  return {
    node,
    tick(frame) {
      const f = frame - o.from;
      spans.forEach((span, i) => {
        if (f === span.from && !live.has(i)) {
          const local = pieceLocal(i);
          const behind = hasBehind && o.behind ? o.behind(span.from, span.dur, local) : null;
          const piece = pieceNode({ clip: o.clip, segIndex: i, mediaUrl: o.mediaUrl, mediaStartMs: o.mediaStartMs, frame: local, cutoutUrl: hasBehind ? o.cutoutUrl : undefined, lite: o.lite, behind });
          const wrapped = transition ? transitionPiece(o.clip, i, span, local, !!o.lite, piece) : piece;
          node.add(wrapped);
          live.set(i, wrapped);
        } else if (f === span.from + span.dur && live.has(i)) {
          live.get(i)!.remove();
          live.delete(i);
        }
      });
    },
  };
}

/** The video frozen on one video-time moment (the cover still behind an opener or an end card, the tail under a transition). */
export function stillNode(o: { clip: Clip; mediaUrl: string; mediaStartMs: number; videoMs: number; cutoutUrl?: string; lite?: boolean }): Node {
  const spans = pieceSpans(o.clip);
  const frame = Math.max(0, msToFrame(o.videoMs));
  let i = spans.findIndex((s, idx) => frame < s.from + Math.max(1, msToFrame(o.clip.segments[idx].outMs - o.clip.segments[idx].inMs)));
  if (i < 0) i = spans.length - 1;
  const local = frame - spans[i].from;
  return pieceNode({ clip: o.clip, segIndex: i, mediaUrl: o.mediaUrl, mediaStartMs: o.mediaStartMs, frame: () => local, still: true, cutoutUrl: o.cutoutUrl, lite: o.lite });
}
